import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { SharedNavbarComponent } from './shared/components/shared-navbar/shared-navbar.component';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { SessionExpiredComponent } from './shared/components/session-expired/session-expired.component';
import { ChatbotComponent } from './modules/cac/components/chatbot/chatbot.component';
import { AuthService } from './shared/service/auth.service';
import { SeoService, SeoData } from './shared/services/seo.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ToastModule,
    SharedNavbarComponent,
    SidebarComponent,
    SessionExpiredComponent,
    ChatbotComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  title = 'frontend';
  isLandingPage = false;
  isStaticPage = false;
  isLoginPage = false;
  isAuthenticated = false;

  // Liste des routes des pages statiques qui utilisent page-layout (avec leur propre navbar)
  staticPageRoutes = [
    // Pages principales
    '/landing',
    '/devis',
    '/qui-sommes-nous',
    '/notre-equipe',
    '/notre-approche',
    '/avantages-courtier-brokins',
    '/blog',
    '/contact',
    '/recrutement',
    '/mentions-legales',
    '/politique-confidentialite',
    '/cgv',
    '/autres-assurances',
    
    // ===== PARTICULIERS =====
    // Santé & Prévoyance
    '/assurance-sante-particuliers',
    '/assurance-prevoyance-particuliers',
    '/assurance-credit-emprunteur',
    '/assurance-prevoyance-deces',
    '/assurance-prevoyance-gav',
    '/assurance-prevoyance-ijh',
    '/assurance-prevoyance-obseque',
    '/assurance-mutuelle-sante-internationale',
    '/assurance-retraite-complementaire-salarie',
    
    // Auto & Moto
    '/assurance-auto-particuliers',
    '/assurance-voyage-personnel',
    '/assurance-moto-personnelle',
    '/assurance-auto-temporaire',
    
    // Habitation & Biens
    '/assurance-biens-particuliers',
    '/assurance-multirisque-habitation',
    '/assurance-multirisque-immeuble-pno',
    '/assurance-dommages-ouvrage',
    '/assurance-loyers-impayes',
    '/assurance-oeuvres-art-bijoux',
    '/assurance-velo-trottinette',
    '/assurance-animaux',
    '/assurance-protection-juridique',
    
    // ===== PROFESSIONNELS =====
    // Santé & Personnes
    '/assurance-sante-professionnels',
    '/assurance-personnes-professionnels',
    '/assurance-mutuelle-sante-internationale-pro',
    '/assurance-prevoyance-professionnelle',
    '/assurance-retraite-complementaire-pro',
    
    // Responsabilité Civile
    '/assurance-rc-professionnelle',
    '/assurance-rc-mandataires-sociaux',
    '/assurance-rc-decennale',
    '/assurance-rc-pro-medicale',
    '/assurance-rc-pro-juridique',
    '/assurance-rc-pro-generale',
    
    // Activité & Protection
    '/assurance-perte-exploitation',
    '/assurance-homme-cle',
    '/assurance-credit-professionnel',
    '/assurance-voyage-professionnel',
    '/assurance-protection-juridique-pro',
    
    // ===== ENTREPRISES =====
    // Collectif & Personnel
    '/assurance-collective-entreprise',
    '/assurance-prevoyance-entreprise',
    '/assurance-retraite-complementaire-entreprise',
    
    // Responsabilité & Activité
    '/assurance-rc-entreprise',
    '/assurance-activite-professionnels',
    '/assurance-cyber-risque',
    '/assurance-bris-machine',
    
    // Biens & Locaux
    '/assurance-biens-professionnels',
    '/assurance-auto-professionnelle',
    '/assurance-flotte-automobile',
    '/assurance-moto-professionnelle',
    '/assurance-flotte-moto-professionnelle',
    '/assurance-locaux-professionnels',
    '/assurance-multirisque-immeuble-pro',
    '/assurance-multirisque-pro',
    
    // Construction
    '/assurance-dommages-ouvrage-maitre',
    '/assurance-decennale-construction',
    '/assurance-tous-risques-chantier',
    
    // Transport
    '/assurance-marchandises-transportees',
    '/assurance-transport'
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService
  ) {}

  ngOnInit() {
    // Vérifier l'authentification initiale
    this.isAuthenticated = this.authService.isAuthenticated();
    
    // Écouter les changements d'authentification
    this.authService.authStatus$.subscribe((isAuth: boolean) => {
      this.isAuthenticated = isAuth;
    });
    
    // Vérifier la route initiale
    const initialUrl = this.router.url || '/';
    this.checkCurrentRoute(initialUrl);

    // Écouter les changements de route
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects || event.url;
      this.checkCurrentRoute(url);
      this.applySeo(url);
    });
  }

  /**
   * Lit `data.seo` de la route active la plus profonde et l'applique aux balises
   * <head>. Sans cela, les ~90 routes partagent le meme <title>Brokins</title>.
   */
  private applySeo(url: string): void {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const data = route.snapshot.data;
    this.seoService.apply(data['seo'] as SeoData | undefined, url.split('?')[0].split('#')[0]);

    // Une route peut declarer explicitement qu'elle porte le chrome public
    // (cas de la 404 : son URL est inconnue par definition, donc absente de
    // `staticPageRoutes`). Sans cela, elle s'affiche avec la sidebar de
    // l'espace connecte.
    if (data['chromePublic'] === true) {
      this.isStaticPage = true;
    }
  }

  private checkCurrentRoute(url: string): void {
    // Enlever les query params et fragments
    const cleanUrl = url.split('?')[0].split('#')[0];
    // Recalculer l'état auth à chaque navigation (important après login via service CAS/CAC)
    this.isAuthenticated = this.authService.isAuthenticated();
    
    // Vérifier si c'est la landing page
    this.isLandingPage = cleanUrl === '/landing' || 
                         cleanUrl === '/landing/' || 
                         cleanUrl === '/' ||
                         cleanUrl === '';
    
    // Vérifier si c'est la page de login
    this.isLoginPage = cleanUrl === '/login' || cleanUrl === '/login/';
    
    // Vérifier si c'est une page statique
    this.isStaticPage = this.staticPageRoutes.some(route => cleanUrl.startsWith(route));
    
    // Debug log (optionnel - vous pouvez commenter en production)
    console.log('Current URL:', url);
    console.log('Clean URL:', cleanUrl);
    console.log('isLandingPage:', this.isLandingPage);
    console.log('isLoginPage:', this.isLoginPage);
    console.log('isStaticPage:', this.isStaticPage);
    console.log('isAuthenticated:', this.isAuthenticated);
  }
}